import { signIn } from "aws-amplify/auth";
import { FormEvent, useCallback, useEffect, useRef, useState } from "react";
import {
  Link,
  createFileRoute,
  useNavigate,
} from "@tanstack/react-router";
import { validate } from "email-validator";
import { z } from "zod";

import { EmailVerificationFields } from "@/components/auth/email-verification-fields";
import {
  SelectedCellRegion,
  type CellLookupHint,
} from "@/components/auth/selected-cell-region";
import { Button } from "@/components/ui/button";
import { Icons } from "@/components/icons";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { hydrateDeflowSession } from "@/lib/browser-auth";
import { formatAuthError, isUserNotConfirmedError } from "@/lib/auth-errors";
import {
  cellConfig,
  configureAmplifyForActiveCell,
  getActiveCellId,
  getCellAuthConfigStatus,
  resolveCellForEmail,
  type CellId,
} from "@/lib/cell";

const signInSearchSchema = z.object({
  email: z.string().optional(),
});

export const Route = createFileRoute("/sign-in")({
  validateSearch: (search) => signInSearchSchema.parse(search),
  component: BrowserSignIn,
});

interface CustomElements extends HTMLFormControlsCollection {
  email: HTMLInputElement;
  password: HTMLInputElement;
}

interface CustomForm extends HTMLFormElement {
  readonly elements: CustomElements;
}

function BrowserSignIn() {
  const navigate = useNavigate();
  const { email: emailFromSearch } = Route.useSearch();
  const [isLoading, setIsLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [verificationEmail, setVerificationEmail] = useState<string | null>(
    null,
  );
  const [verifiedNotice, setVerifiedNotice] = useState<string | null>(null);
  const [activeCellId, setActiveCellId] = useState<CellId>(getActiveCellId);
  const [lookupHint, setLookupHint] = useState<CellLookupHint>({ status: "idle" });
  const lookupAbortRef = useRef<AbortController | null>(null);

  const runEmailLookup = useCallback(async (rawEmail: string) => {
    const email = rawEmail.trim().toLowerCase();
    if (!validate(email)) {
      setLookupHint({ status: "idle" });
      return;
    }

    lookupAbortRef.current?.abort();
    const controller = new AbortController();
    lookupAbortRef.current = controller;

    setLookupHint({ status: "loading" });
    try {
      const { cellId, source } = await resolveCellForEmail(
        email,
        controller.signal,
      );
      if (controller.signal.aborted) return;

      setActiveCellId(cellId);
      setLookupHint(
        source === "lookup" ? { status: "matched" } : { status: "not-found" },
      );
    } catch {
      if (!controller.signal.aborted) {
        setLookupHint({ status: "idle" });
      }
    }
  }, []);

  useEffect(() => {
    const prefilled = emailFromSearch?.trim().toLowerCase();
    if (prefilled && validate(prefilled)) {
      void runEmailLookup(prefilled);
    }
  }, [emailFromSearch, runEmailLookup]);

  useEffect(() => {
    return () => lookupAbortRef.current?.abort();
  }, []);

  const onSubmit = async (event: FormEvent<CustomForm>) => {
    event.preventDefault();
    setFormError(null);
    setVerifiedNotice(null);

    const emailRaw = event.currentTarget.elements.email.value;
    const email = emailRaw.trim().toLowerCase();
    const password = event.currentTarget.elements.password.value;

    if (!validate(email) || password.length === 0) {
      setFormError("Enter a valid email and password.");
      return;
    }

    setIsLoading(true);
    try {
      const { cellId, source } = await resolveCellForEmail(email);
      setActiveCellId(cellId);
      setLookupHint(
        source === "lookup" ? { status: "matched" } : { status: "not-found" },
      );
      configureAmplifyForActiveCell();
      const authReady = getCellAuthConfigStatus();
      if (!authReady.ok) {
        setFormError(authReady.message);
        setIsLoading(false);
        return;
      }

      const result = await signIn({ username: email, password });
      const step = result.nextStep?.signInStep;
      if (step === "CONFIRM_SIGN_UP") {
        setVerificationEmail(email);
        setIsLoading(false);
        return;
      }
      if (step && step !== "DONE") {
        setFormError(
          `Further sign-in steps are required (${step}). Contact support if this persists.`,
        );
        setIsLoading(false);
        return;
      }

      await hydrateDeflowSession();
      navigate({ to: "/dashboard", replace: true });
    } catch (err) {
      if (isUserNotConfirmedError(err)) {
        setVerificationEmail(email);
        setIsLoading(false);
        return;
      }
      setFormError(formatAuthError(err));
    } finally {
      setIsLoading(false);
    }
  };

  cellConfig();

  return (
    <main className="flex flex-col items-center justify-center gap-8 min-h-screen px-6">
      <img src="/deflow.png" alt="Deflow logo" width={180} height={37} />
      <SelectedCellRegion
        nextPath="/sign-in"
        variant="sign-in"
        cellId={activeCellId}
        lookupHint={lookupHint}
      />

      <form className="w-full max-w-sm space-y-4" onSubmit={onSubmit}>
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            defaultValue={verificationEmail ?? emailFromSearch ?? undefined}
            key={verificationEmail ?? emailFromSearch ?? "sign-in-email"}
            onBlur={(event) => void runEmailLookup(event.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password">Password</Label>
          <Input
            id="password"
            name="password"
            type="password"
            autoComplete="current-password"
          />
        </div>
        {formError ? (
          <p className="text-sm text-destructive" role="alert">
            {formError}
          </p>
        ) : null}
        {verifiedNotice ? (
          <p className="text-sm text-muted-foreground" role="status">
            {verifiedNotice}
          </p>
        ) : null}
        <Button className="w-full" type="submit" disabled={isLoading}>
          {isLoading ? (
            <Icons.spinner className="h-4 w-4 animate-spin" />
          ) : (
            "Sign in"
          )}
        </Button>
      </form>

      {verificationEmail ? (
        <div className="w-full max-w-sm">
          <EmailVerificationFields
            email={verificationEmail}
            prompt="Your account still needs email verification before you can sign in."
            onVerified={() => {
              setVerificationEmail(null);
              setVerifiedNotice(
                "Email verified. Sign in with your password to continue.",
              );
            }}
          />
        </div>
      ) : null}

      <p className="text-sm text-muted-foreground text-center">
        No account?{" "}
        <Link to="/sign-up" className="underline">
          Create one
        </Link>
      </p>
    </main>
  );
}
